import {
  Button,
  Col,
  Divider,
  List,
  notification,
  Row,
  Table,
  UploadFile,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { UploadChangeParam } from 'antd/lib/upload';
import { DownloadCsvTemplateCard, ImportCsvDataCard } from 'components';
import { useState } from 'react';
import { importSupplier, ISupplierFile } from 'services/supplier';
import { readCSV } from 'utilities/file';
import { findDuplicate } from 'utilities/shared';

export const ImportSuppliers: React.FC<{ redirection?: boolean }> = ({ redirection = true }) => {
  const [parsedCSVData, setParsedCSVData] = useState<ISupplierFile[]>([]);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  const { list } = useNavigation();

  const onFileUploadChange = async (info: UploadChangeParam<UploadFile<any>>) => {
    try {
      if (!info.fileList.length) {
        return;
      }

      let rowNumber = 0;
      const file = info.fileList[0].originFileObj!;
      const data: ISupplierFile[] = await readCSV({
        file,
        transformFunction: (row: any) => {
          rowNumber += 1;

          if (!row.name) {
            throw new Error(`'name' has missing values in row ${rowNumber}`);
          }

          if (!row.supplierCode) {
            throw new Error(`'supplierCode' has missing values in row ${rowNumber}`);
          }

          const transformedRow: ISupplierFile = {
            name: row.name.trim(),
            supplierCode: row.supplierCode.trim(),
          };
          if (row.email) {
            transformedRow.email = row.email.trim();
          }
          if (row.mobileNumber) {
            transformedRow.mobileNumber = row.mobileNumber.trim();
          }
          if (row.street || row.area || row.city || row.country || row.zip) {
            const address = {
              street: row.street.trim(),
              area: row.area.trim(),
              city: row.city.trim(),
              country: row.country.trim(),
              zip: row.zip.trim(),
            };
            transformedRow.address = address;
          }

          return transformedRow;
        },
        validateFunction: (row: any) => {
          const requiredFields = ['name', 'supplierCode'];
          const keys = Object.keys(row);
          return requiredFields.every((field) => keys.includes(field));
        },
      });

      const duplicateSupplierCode = findDuplicate(data, 'supplierCode');
      const uniqueDuplicates = Array.from(new Set(duplicateSupplierCode));
      if (duplicateSupplierCode.length) {
        notification.error({
          message: 'Failed',
          description: `Found ${
            duplicateSupplierCode.length
          } duplicate values for 'supplierCode' - ${uniqueDuplicates.join(', ')}`,
        });
        return;
      }

      setParsedCSVData(data);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
  };

  const onFileRemove = (_file: UploadFile<any>) => {
    setParsedCSVData([]);
  };

  const onSubmitButtonClick = async () => {
    setIsButtonLoading(true);
    try {
      await importSupplier(parsedCSVData);
      if (redirection) list('suppliers');
      notification.success({
        message: 'Success',
        description: 'Successfully imported suppliers',
      });
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsButtonLoading(false);
  };

  const getCsvRowsForTemplate = () => {
    const rows = [
      ['name', 'supplierCode', 'email', 'mobileNumber', 'street', 'area', 'city', 'country', 'zip'],
    ];
    const result = [
      {
        name: 'Supplier 100',
        supplierCode: 'S100',
        email: 's100@mail.com',
        mobileNumber: '00010010010',
        street: 'Rankin Street',
        area: 'Wari',
        city: 'Dhaka',
        country: 'Bangladesh',
        zip: '1200',
      },
      {
        name: 'Supplier 200',
        supplierCode: 'S200',
        email: 's200@mail.com',
        mobileNumber: '00020020020',
        street: 'Rankin Street',
        area: 'Wari',
        city: 'Dhaka',
        country: 'Bangladesh',
        zip: '1200',
      },
    ];
    result.forEach((elem) => {
      rows.push([
        elem.name,
        elem.supplierCode,
        elem.email,
        elem.mobileNumber,
        elem.street,
        elem.area,
        elem.city,
        elem.country,
        elem.zip,
      ]);
    });

    return rows;
  };

  const flatParsedCsvData = parsedCSVData.map(
    ({ name, supplierCode, email, mobileNumber, address }) => {
      const flatObject: Record<string, string | undefined> = {
        key: supplierCode,
        name,
        supplierCode,
        email,
        mobileNumber,
        street: address?.street,
        area: address?.area,
        city: address?.city,
        country: address?.country,
        zip: address?.zip,
      };

      return flatObject;
    },
  );

  const flatCsvObject = flatParsedCsvData[0] || {};
  const columns = [];
  for (const [key] of Object.entries(flatCsvObject)) {
    if (key === 'key') {
      continue;
    }
    columns.push({ title: key, key, dataIndex: key });
  }

  return (
    <List title="Import Suppliers">
      <Row gutter={[48, 16]} wrap style={{ marginBottom: '2rem' }}>
        <Col xs={11} lg={8}>
          <DownloadCsvTemplateCard
            rows={getCsvRowsForTemplate()}
            title="Download .csv template for import"
          />
        </Col>

        <Col xs={11} sm={11} lg={8}>
          <ImportCsvDataCard onFileUploadChange={onFileUploadChange} onFileRemove={onFileRemove} />
        </Col>
      </Row>

      <Divider />
      {/* CSV Data Table */}
      <Row wrap={false}>
        <Col flex="auto">
          <Table columns={columns} dataSource={flatParsedCsvData} />
        </Col>
      </Row>
      {/* Submit Button */}
      <Row justify="center" align="middle" style={{ marginTop: '1rem' }}>
        <Button
          style={{ width: '300px' }}
          loading={isButtonLoading}
          disabled={!parsedCSVData.length}
          onClick={onSubmitButtonClick}
        >
          Save
        </Button>
      </Row>
    </List>
  );
};
